// The CMS reader's pure parts (#160 WP7): the shapes the components render,
// and every decision that does not need a component — which columns show,
// their grid, where a redirect goes, what a lock card says, what an inline
// figure looks like. Kept out of the .vue files so the tests can pin them.
import type { CmsBoxBackground, CmsBoxZone, CmsLockReason } from 'src/graphql/graphql'
import { isSitePath, type SiteMenuSection } from 'src/core/siteMenu'

// ── shapes ──────────────────────────────────────────────────────────────────
// Structural, so every nesting level of the generated CmsPage type fits (each
// level is its own fragment, cms.operations.ts). The deepest level selects no
// nested box, hence `box?`.

export interface CmsImageView {
  id: string
  alt: string
  caption: string
  credit: string
  width?: number | null
  height?: number | null
  document: { id: string; downloadUrl: string | null }
}

// No id: CmsFile.id is not @public, and the reader's anonymous query may only
// select public fields — the editor reads it through CmsEdItemFields.
export interface CmsFileView {
  title: string
  description: string
  url: string
  mime: string
  size?: number | null
}

export type CmsItemView =
  | {
      __typename: 'CmsTextItem'
      key: string
      heading: string
      html: string
      images: readonly CmsImageView[]
    }
  | { __typename: 'CmsFactsItem'; key: string; heading: string; html: string }
  | {
      __typename: 'CmsCtaItem'
      key: string
      heading: string
      html: string
      label: string
      href: string
    }
  | { __typename: 'CmsImageItem'; key: string; images: readonly CmsImageView[]; zoom: boolean }
  | { __typename: 'CmsFilesItem'; key: string; heading: string; files: readonly CmsFileView[] }
  | { __typename: 'CmsModuleItem'; key: string; heading: string; module: string; config: unknown }
  | {
      __typename: 'CmsVideoItem'
      key: string
      heading: string
      videoId: string
      start?: number | null
      caption: string
      /** Always https://www.youtube-nocookie.com/embed/<id>[?start=s]. */
      embedUrl: string
    }
  | { __typename: 'CmsSignInItem'; key: string; hiddenCount: number }
  | { __typename: 'CmsBoxItem'; key: string; box?: CmsBoxView }

export interface CmsColumnView {
  key: string
  width: number
  items: readonly CmsItemView[]
}

export interface CmsBoxView {
  key: string
  zone: CmsBoxZone
  background: CmsBoxBackground
  columns: readonly CmsColumnView[]
}

export type CmsEntryView =
  | ({ __typename: 'CmsBox' } & CmsBoxView)
  | { __typename: 'CmsSignIn'; key: string; hiddenCount: number }

export interface CmsPageLink {
  id: string
  kind: 'PAGE' | 'LINK' | 'SPACER' | 'SECTION' | 'APP'
  path: string | null
  title: string
  navTitle: string
  externalUrl: string | null
  lock?: { reason: CmsLockReason } | null
}

// ── boxes and columns ───────────────────────────────────────────────────────

/** Whether an item puts anything on screen. A nested box counts only when it
 *  has something to show itself; one past the selection floor (no `box`)
 *  shows nothing. */
export function itemShows(item: CmsItemView): boolean {
  if (item.__typename === 'CmsBoxItem') return !!item.box && boxShows(item.box)
  return true
}

/** The columns a reader sees: empty ones drop out (AF27 — the server drops
 *  columns whose items were all filtered; this also drops those holding only
 *  boxes that show nothing). */
export function visibleColumns(box: CmsBoxView): CmsColumnView[] {
  return box.columns
    .map((c) => ({ ...c, items: c.items.filter(itemShows) }))
    .filter((c) => c.items.length > 0)
}

export function boxShows(box: CmsBoxView): boolean {
  return visibleColumns(box).length > 0
}

/** The grid template for a box's columns: the stored percentages as `fr`, so
 *  the remaining columns share the row in the same proportions when one has
 *  dropped out. */
export function columnTemplate(cols: readonly { width: number }[]): string {
  return cols.map((c) => `${Math.max(1, Math.round(c.width * 10) / 10)}fr`).join(' ')
}

/** A box renders as plain content (no grid, no band) when it is one column
 *  without a background — it looks like the content it holds. */
export function isPlainBox(box: CmsBoxView, cols: readonly CmsColumnView[]): boolean {
  return cols.length === 1 && box.background === 'NONE'
}

/** The class for a box's background, or '' for none. */
export function backgroundClass(bg: CmsBoxBackground): string {
  return bg === 'TINT' ? 'cms-bg-tint' : bg === 'ACCENT' ? 'cms-bg-accent' : ''
}

/** How many box levels a box holds, itself included (1 = no nested box);
 *  the reader's selection reaches CMS_MAX_DEPTH (cms.operations.ts). */
export function boxDepth(box: CmsBoxView): number {
  let deepest = 0
  for (const c of box.columns)
    for (const i of c.items)
      if (i.__typename === 'CmsBoxItem' && i.box) deepest = Math.max(deepest, boxDepth(i.box))
  return deepest + 1
}

/** Split a page's top-level entries into the two zones. Sign-in placeholders
 *  stand in for main-zone boxes only: gated side boxes are absent without one
 *  (AF3). */
export function splitZones(entries: readonly CmsEntryView[]): {
  main: CmsEntryView[]
  side: CmsBoxView[]
} {
  const main: CmsEntryView[] = []
  const side: CmsBoxView[] = []
  for (const e of entries) {
    if (e.__typename === 'CmsSignIn') main.push(e)
    else if (!boxShows(e)) continue
    else if (e.zone === 'SIDE') side.push(e)
    else if (e.zone === 'MAIN') main.push(e)
  }
  return { main, side }
}

/** An app page's areas (#160 AF30): TOP above the page's data, BOTTOM
 *  below it, SIDE in a side column beside it. */
export interface AppAreas {
  top: CmsEntryView[]
  side: CmsBoxView[]
  bottom: CmsEntryView[]
}

/**
 * Split an app page's entries into its areas. A sign-in placeholder carries
 * no zone: it stands in front of the next box shown (the server emits a
 * zone's placeholders among that zone's boxes), else after the last one;
 * the side column gets none (AF3, as on content pages).
 */
export function splitAppZones(entries: readonly CmsEntryView[]): AppAreas {
  const out: AppAreas = { top: [], side: [], bottom: [] }
  const zoneAt = (i: number): CmsBoxZone => {
    for (let j = i + 1; j < entries.length; j++) {
      const e = entries[j]!
      if (e.__typename === 'CmsBox' && e.zone !== 'SIDE') return e.zone
    }
    for (let j = i - 1; j >= 0; j--) {
      const e = entries[j]!
      if (e.__typename === 'CmsBox' && e.zone !== 'SIDE') return e.zone
    }
    return 'TOP'
  }
  entries.forEach((e, i) => {
    if (e.__typename === 'CmsSignIn') {
      ;(zoneAt(i) === 'BOTTOM' ? out.bottom : out.top).push(e)
      return
    }
    if (!boxShows(e)) return
    if (e.zone === 'SIDE') out.side.push(e)
    else if (e.zone === 'BOTTOM') out.bottom.push(e)
    else if (e.zone === 'TOP') out.top.push(e)
    // MAIN never sits on an app page (the cms service refuses it).
  })
  return out
}

/** The page's in-page sections (module `pages.sections`): every headed text
 *  item, depth first, in reading order. */
export function pageSections(entries: readonly CmsEntryView[]): { key: string; heading: string }[] {
  const out: { key: string; heading: string }[] = []
  const walkBox = (b: CmsBoxView) => {
    for (const c of b.columns)
      for (const i of c.items) {
        if (i.__typename === 'CmsBoxItem') {
          if (i.box) walkBox(i.box)
        } else if (
          (i.__typename === 'CmsTextItem' || i.__typename === 'CmsFilesItem') &&
          i.heading
        ) {
          out.push({ key: i.key, heading: i.heading })
        }
      }
  }
  for (const e of entries) if (e.__typename === 'CmsBox' && e.zone === 'MAIN') walkBox(e)
  return out
}

/** The DOM id of an item, for in-page links. */
export function itemAnchor(key: string): string {
  return `cms-${key}`
}

export interface ChildLink {
  id: string
  label: string
  locked: boolean
  internal?: string
  external?: string
}

/** Child pages as links: in-app for a page (or a LINK into the site), out
 *  for a LINK elsewhere; spacers and sections without a page are skipped. */
export function childPageLinks(children: readonly CmsPageLink[], origin: string): ChildLink[] {
  const out: ChildLink[] = []
  for (const c of children) {
    const base = { id: c.id, label: c.navTitle || c.title, locked: !!c.lock }
    if (c.kind === 'PAGE' && c.path) out.push({ ...base, internal: c.path })
    else if (c.kind === 'LINK' && c.externalUrl) {
      const t = redirectTarget(c.externalUrl, origin)
      if (t) out.push({ ...base, ...t })
    }
  }
  return out
}

/** One row of a page's "More in <section>" panel. */
export interface SectionLink extends ChildLink {
  /** The page being read (aria-current). */
  current: boolean
}

/** How many of the section's pages the panel lists (the prototype's 8). */
export const MORE_IN_MAX = 8

/**
 * The side panel's "More in <section>" list (prototype `more`): the pages of
 * the page's top-level menu section, as the CMS menu files them, up to
 * MORE_IN_MAX — the page itself marked current — then any child page of
 * the page that list does not already hold, so no way down is lost. With no
 * section (the menu has not answered, or the page is outside the main menu)
 * the children alone.
 */
export function moreInSection(
  section: SiteMenuSection | null,
  children: readonly ChildLink[],
  currentPath: string,
): SectionLink[] {
  const out: SectionLink[] = []
  const seen = new Set<string>()
  const push = (l: ChildLink) => {
    // `https://x.org` and `https://x.org/` are one address (URL.href adds the slash).
    const target = l.internal
      ? normalizeCmsPath(l.internal)
      : (l.external ?? '').replace(/\/+$/, '')
    if (!target || seen.has(target)) return
    seen.add(target)
    out.push({ ...l, current: !!l.internal && target === currentPath })
  }
  for (const g of section?.groups ?? [])
    for (const l of g.links) {
      if (out.length >= MORE_IN_MAX) break
      const base = { id: '', label: l.label, locked: !!l.locked }
      if (typeof l.to === 'string') push({ ...base, id: l.to, internal: l.to })
      else if (l.href) push({ ...base, id: l.href, external: l.href })
    }
  for (const c of children) push(c)
  return out
}

// ── redirects (AF25) and LINK pages ─────────────────────────────────────────

export type RedirectTarget = { internal: string } | { external: string }

/**
 * Where a redirect (or a LINK page) leads: a path on this site is an in-app
 * navigation, an absolute URL on this origin too; any other http(s) URL leaves
 * the SPA. Anything else (javascript:, mailto:, garbage) goes nowhere — the
 * caller shows "not found" rather than following it.
 */
export function redirectTarget(location: string, origin: string): RedirectTarget | null {
  const loc = location.trim()
  if (!loc) return null
  if (isSitePath(loc)) return { internal: loc }
  let url: URL
  try {
    url = new URL(loc)
  } catch {
    return null
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
  if (url.origin === origin) return { internal: `${url.pathname}${url.search}${url.hash}` }
  return { external: url.href }
}

/** Trailing slashes do not make a different page (`/fsg/map/` = `/fsg/map`). */
export function normalizeCmsPath(path: string): string {
  return path.replace(/\/+$/, '') || '/'
}

/** Whether a failed result carries `code` — on the error itself or wrapped
 *  by the router's subgraph envelope (extensions.errors[]). */
export function hasErrorCode(error: unknown, code: string): boolean {
  const list = (error as { graphQLErrors?: unknown } | null)?.graphQLErrors
  if (!Array.isArray(list)) return false
  const codeOf = (e: unknown) => (e as { extensions?: { code?: unknown } } | null)?.extensions?.code
  return list.some((e) => {
    if (codeOf(e) === code) return true
    const nested = (e as { extensions?: { errors?: unknown } }).extensions?.errors
    return Array.isArray(nested) && nested.some((n) => codeOf(n) === code)
  })
}

/** The field errors a refused mutation carries (`extensions.field`, also
 *  inside the router's envelope), each with its message minus the
 *  "<field>: " prefix the cms service puts in front. */
export function errorFields(error: unknown): { field: string; message: string }[] {
  const list = (error as { graphQLErrors?: unknown } | null)?.graphQLErrors
  if (!Array.isArray(list)) return []
  const out: { field: string; message: string }[] = []
  const take = (e: unknown) => {
    const x = e as { message?: unknown; extensions?: { field?: unknown; errors?: unknown } } | null
    const field = x?.extensions?.field
    if (typeof field === 'string' && field) {
      const msg = typeof x?.message === 'string' ? x.message : ''
      out.push({
        field,
        message: msg.startsWith(`${field}: `) ? msg.slice(field.length + 2) : msg,
      })
    }
    const nested = x?.extensions?.errors
    if (Array.isArray(nested)) nested.forEach(take)
  }
  list.forEach(take)
  return out
}

/** Navigating away from the SPA — a seam so tests can watch it. */
export const cmsWindow = {
  replace(url: string): void {
    window.location.replace(url)
  },
}

// ── locks (AF3, AF14 — owner rulings on the WP2 behaviour list) ─────────────

export interface LockCopy {
  title: string
  text: string
  /** Offer the sign-in (a signed-out visitor who may open it after signing in). */
  signIn: boolean
}

export function lockCopy(reason: CmsLockReason): LockCopy {
  switch (reason) {
    case 'SIGN_IN_REQUIRED':
      return {
        title: 'Sign in to see this page',
        text: 'This page is only available to signed-in users with access. Sign in to check whether you can see it.',
        signIn: true,
      }
    case 'NOT_IN_AUDIENCE':
      return {
        title: 'Only for selected groups',
        text: 'You are signed in, but this page is for a group you are not part of. If you think you should see it, contact the event office.',
        signIn: false,
      }
    case 'SIGNED_OUT_ONLY':
      return {
        title: 'Only for visitors who are not signed in',
        text: 'This page is hidden after sign-in.',
        signIn: false,
      }
  }
}

// ── modules (AF12) ──────────────────────────────────────────────────────────

/** The data modules the reader knows. Anything else — `legacy:<plugin>`
 *  placeholders and keys from a newer editor — renders the neutral box. */
export const KNOWN_MODULES = [
  'docs.section',
  'pages.children',
  'pages.sections',
  // The partner wall (#168 AP6; config {group, compact}).
  'partners.logos',
  'images.gallery',
  // The News module's list box (#162 AH7; config {categoryIds, seasons,
  // limit, layout}, coordinator decision D6).
  'news.list',
  // The event map (#169 AO4; config {eventId?, pit?}): the map reader boxed.
  'map.event',
] as const
export type KnownModule = (typeof KNOWN_MODULES)[number]

export function knownModule(key: string): KnownModule | null {
  return (KNOWN_MODULES as readonly string[]).includes(key) ? (key as KnownModule) : null
}

/** A boolean field of a module's config: true only when it is `true`. */
export function configFlag(config: unknown, field: string): boolean {
  if (!config || typeof config !== 'object') return false
  return (config as Record<string, unknown>)[field] === true
}

/** A string field of a module's config, or null. */
export function configString(config: unknown, field: string): string | null {
  if (!config || typeof config !== 'object') return null
  const v = (config as Record<string, unknown>)[field]
  return typeof v === 'string' && v ? v : null
}

// ── files ───────────────────────────────────────────────────────────────────

const MIME_LABEL: Record<string, string> = {
  'application/pdf': 'PDF',
  'application/zip': 'ZIP',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'text/plain': 'TXT',
  'text/csv': 'CSV',
}

/** A file type a reader recognises: `PDF`, `JPEG`, … */
export function mimeLabel(mime: string): string {
  const known = MIME_LABEL[mime.toLowerCase()]
  if (known) return known
  const sub = mime.split('/')[1] ?? mime
  return sub.replace(/^x-/, '').split(/[.+;]/)[0]!.toUpperCase() || 'FILE'
}

// ── rich text ───────────────────────────────────────────────────────────────

const FIGURE_SIZES = new Set(['s', 'm', 'l', 'full'])
const FIGURE_ALIGNS = new Set(['left', 'center', 'right'])

/**
 * Lay out server-sanitised HTML for reading. The cms service is the only
 * source of this HTML and sanitises it against its allow-list on save (AF27;
 * no script, iframe, style or event handler survives), so this adds structure
 * and never removes danger:
 *  - inline figures (`<figure data-img data-size data-align>`) get the reader
 *    class, a lazy image (the server filled in its src; a missing one comes
 *    from the image record), a caption and the image record's credit line;
 *    an unknown size or alignment falls back to m / center; unless the
 *    editor switched click to enlarge off (`data-zoom="off"`), the image
 *    sits in a `.cms-zoom` button that CmsRichText opens in the viewer;
 *  - tables scroll inside their own box on narrow screens;
 *  - links that leave the site open in a new tab.
 */
export function layoutRichText(
  html: string,
  images: readonly CmsImageView[],
  origin: string,
  opts: { facts?: boolean } = {},
): string {
  if (!html) return ''
  const tpl = document.createElement('template')
  tpl.innerHTML = html
  const byId = new Map(images.map((i) => [i.id, i]))

  for (const fig of Array.from(tpl.content.querySelectorAll('figure[data-img]'))) {
    const rec = byId.get(fig.getAttribute('data-img') ?? '')
    fig.classList.add('cms-fig')
    if (!FIGURE_SIZES.has(fig.getAttribute('data-size') ?? '')) fig.setAttribute('data-size', 'm')
    if (!FIGURE_ALIGNS.has(fig.getAttribute('data-align') ?? ''))
      fig.setAttribute('data-align', 'center')
    let img = fig.querySelector('img')
    if (!img && rec?.document.downloadUrl) {
      img = document.createElement('img')
      img.setAttribute('src', rec.document.downloadUrl)
      fig.insertBefore(img, fig.firstChild)
    }
    if (!img) {
      fig.remove()
      continue
    }
    if (!img.getAttribute('alt') && rec?.alt) img.setAttribute('alt', rec.alt)
    if (!img.hasAttribute('alt')) img.setAttribute('alt', '')
    img.setAttribute('loading', 'lazy')
    img.setAttribute('decoding', 'async')
    if (rec?.width && rec.height) {
      img.setAttribute('width', String(rec.width))
      img.setAttribute('height', String(rec.height))
    }
    let cap = fig.querySelector('figcaption')
    if (fig.getAttribute('data-zoom') !== 'off' && !img.closest('.cms-zoom')) {
      const alt = img.getAttribute('alt') ?? ''
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'cms-zoom'
      btn.setAttribute('aria-label', alt ? `Enlarge image: ${alt}` : 'Enlarge image')
      btn.setAttribute(
        'data-zoom-title',
        cap?.textContent?.trim() || rec?.caption || alt || 'Image',
      )
      img.parentNode!.insertBefore(btn, img)
      btn.appendChild(img)
    }
    if (!cap && (rec?.caption || rec?.credit)) {
      cap = document.createElement('figcaption')
      if (rec.caption) cap.textContent = rec.caption
      fig.appendChild(cap)
    }
    if (cap && rec?.credit && !cap.querySelector('.cms-credit')) {
      const credit = document.createElement('span')
      credit.className = 'cms-credit'
      credit.textContent = rec.credit
      cap.appendChild(credit)
    }
  }

  for (const table of Array.from(tpl.content.querySelectorAll('table'))) {
    if (table.parentElement?.classList.contains('cms-tw')) continue
    const wrap = document.createElement('div')
    wrap.className = 'cms-tw'
    table.parentNode!.insertBefore(wrap, table)
    wrap.appendChild(table)
  }

  for (const a of Array.from(tpl.content.querySelectorAll('a[href]'))) {
    const t = redirectTarget(a.getAttribute('href') ?? '', origin)
    if (t && 'external' in t) {
      a.setAttribute('target', '_blank')
      a.setAttribute('rel', 'noopener')
    }
  }

  if (opts.facts) layoutFacts(tpl.content)

  const out = document.createElement('div')
  out.appendChild(tpl.content)
  return out.innerHTML
}

/**
 * Side facts (FACTS items) read as the prototype's "At a glance" rows: a
 * list entry or paragraph that starts with a bold label (`<li><strong>Venue
 * </strong> Hockenheimring</li>`, the shape the importer and the editor
 * write) becomes a row — the label muted on the left, the rest as the value
 * on the right, wrapping under the label when it is long. Anything else
 * stays as written.
 */
function layoutFacts(root: DocumentFragment): void {
  for (const row of Array.from(root.querySelectorAll('li, p'))) {
    let first = row.firstChild
    while (first && first.nodeType === Node.TEXT_NODE && !first.textContent?.trim())
      first = first.nextSibling
    if (!(first instanceof Element) || !['B', 'STRONG'].includes(first.tagName)) continue
    const after: ChildNode[] = []
    for (let n = first.nextSibling; n; n = n.nextSibling) after.push(n)
    if (!after.some((n) => n.textContent?.trim())) continue
    const value = document.createElement('span')
    value.className = 'cms-fact__v'
    for (const n of after) value.appendChild(n)
    if (value.firstChild?.nodeType === Node.TEXT_NODE)
      value.firstChild.textContent = value.firstChild.textContent!.replace(/^\s+/, '')
    first.classList.add('cms-fact__k')
    row.classList.add('cms-fact')
    row.appendChild(value)
  }
}
